import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';




export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    caption: '',
    category: undefined,
    location_id: undefined
  });
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchImages();
    fetchLocations();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await apiClient.get('/gallery');
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiClient.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        location_id: formData.location_id || null
      };

      await apiClient.post('/admin/gallery', payload);
      toast.success('Image added successfully!');

      setIsModalOpen(false);
      resetForm();
      fetchImages();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await apiClient.delete(`/admin/gallery/${id}`);
        toast.success('Image deleted successfully!');
        fetchImages();
      } catch (error) {
        toast.error('Failed to delete image');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      url: '',
      caption: '',
      category: undefined,
      location_id: undefined
    });
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : '';
  };

  const filteredImages = filterCategory === 'all' 
    ? images 
    : images.filter(img => img.category === filterCategory);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Gallery</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Image
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Label>Filter by Category</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Cabins">Cabins</SelectItem>
              <SelectItem value="Meeting Rooms">Meeting Rooms</SelectItem>
              <SelectItem value="Lounge">Lounge</SelectItem>
              <SelectItem value="Events">Events</SelectItem>
              <SelectItem value="Common Areas">Common Areas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow-md overflow-hidden group relative">
              <img src={image.url} alt={image.caption} className="w-full h-48 object-cover" />
              <div className="p-3">
                <p className="text-sm font-semibold text-gray-900">{image.caption}</p>
                <p className="text-xs text-gray-500">{image.category}</p>
                {image.location_id && (
                  <p className="text-xs text-gray-500">{getLocationName(image.location_id)}</p>
                )}
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button 
                  onClick={() => handleDelete(image.id)} 
                  size="sm" 
                  variant="destructive"
                  className="shadow-lg"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Image</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUpload
              label="Gallery Image"
              value={formData.url}
              onChange={(url) => setFormData({ ...formData, url: url })}
              required={true}
              preview={true}
            />
            <div>
              <Label>Caption</Label>
              <Input
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Image description"
                required
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cabins">Cabins</SelectItem>
                  <SelectItem value="Meeting Rooms">Meeting Rooms</SelectItem>
                  <SelectItem value="Lounge">Lounge</SelectItem>
                  <SelectItem value="Events">Events</SelectItem>
                  <SelectItem value="Common Areas">Common Areas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location (Optional)</Label>
              <Select value={formData.location_id} onValueChange={(value) => setFormData({ ...formData, location_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Add Image
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}